import './ShipmentAddressForm.scss';

import { useTable } from '@refinedev/antd';
import { Form, Input, SelectProps, Table } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import { ShippingLabel } from '@/model/Logistic/ShippingLabel/ShippingLabel';
import { RESOURCES } from '@/Resources';
import { Country, phoneNumberMeta } from '@/utils/phoneNumbers';

interface ShipmentAddressFormProps {
  formProps: FormProps;
  localidadesSelectProps: SelectProps;
  paisesSelectProps: SelectProps;
  provinciasSelectProps: SelectProps;
  setSaveAddress: (value: boolean) => void;
  initialValuesSetted?: boolean;
}

const ShipmentAddressForm = (props: ShipmentAddressFormProps) => {
  const { t } = useTranslation();
  const {
    formProps,
    paisesSelectProps,
    provinciasSelectProps,
    localidadesSelectProps,
    setSaveAddress,
    initialValuesSetted,
  } = props;
  const { form } = formProps;
  const [selectedAddress, setSelectedAddress] = useState<ShippingLabel | undefined>();
  const [localidadesOptions, setLocalidadesOptions] = useState<SelectProps['options']>(
    localidadesSelectProps.options || [],
  );
  // Watchers
  const company = Form.useWatch('company', form);
  const pais = Form.useWatch('receiver_pais', form);
  const provincia = Form.useWatch('receiver_provincia', form);
  const localidad = Form.useWatch('receiver_localidad', form);
  const address = Form.useWatch('receiver_address', form);
  const email = Form.useWatch('receiver_email', form);
  const phone = Form.useWatch('receiver_phone', form);
  const name = Form.useWatch('receiver_name', form);
  const postalCode = Form.useWatch('receiver_postal_code', form);
  // Refs para reseteos
  const prevPais = useRef<any>();
  const prevProvincia = useRef<any>();
  const prevLocalidad = useRef<any>();
  const lockReset = useRef(true);
  const { tableProps } = useTable<ShippingLabel>({
    resource: RESOURCES.SHIPPING_LABELS,
    pagination: { pageSize: 20 },
    meta: {
      empresa: company,
    },
  });
  const countryCode = useMemo(() => Country[pais as keyof typeof Country], [pais]);
  const { dataSource } = tableProps;

  useEffect(() => {
    if (selectedAddress) {
      if (
        pais !== selectedAddress.localidad.provincia.pais.id ||
        provincia !== selectedAddress.localidad.provincia.id ||
        localidad !== selectedAddress.localidad.id ||
        address !== selectedAddress.address ||
        email !== selectedAddress.email ||
        phone !== selectedAddress.phone_number ||
        name !== selectedAddress.contact ||
        postalCode !== selectedAddress.postal_code
      ) {
        setSelectedAddress(undefined);
        setSaveAddress(true);
      } else {
        setSaveAddress(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, provincia, localidad, address, email, phone, name, postalCode]);

  useEffect(() => {
    if (initialValuesSetted && form) {
      prevPais.current = form.getFieldValue('receiver_pais');
      prevProvincia.current = form.getFieldValue('receiver_provincia');
      prevLocalidad.current = form.getFieldValue('receiver_localidad');
      lockReset.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValuesSetted]);

  // Reseteo de campos
  useEffect(() => {
    if (form && !lockReset.current) {
      if (pais !== prevPais.current) {
        form.setFieldsValue({
          receiver_provincia: undefined,
          receiver_localidad: undefined,
          receiver_postal_code: undefined,
        });
        prevPais.current = pais;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais]);
  useEffect(() => {
    if (form && !lockReset.current) {
      if (provincia !== prevProvincia.current) {
        form.setFieldsValue({
          receiver_localidad: undefined,
          receiver_postal_code: undefined,
        });
        prevProvincia.current = provincia;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provincia]);
  useEffect(() => {
    if (form && !lockReset.current) {
      if (localidad !== prevLocalidad.current) {
        if (!localidad) {
          form.setFieldsValue({
            receiver_postal_code: undefined,
          });
        }

        prevLocalidad.current = localidad;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localidad]);

  useEffect(() => {
    if (!dataSource || dataSource.length === 0) {
      setSaveAddress(true);
    }
  }, [dataSource, form, selectedAddress, setSaveAddress]);

  useEffect(() => {
    if (form && localidadesSelectProps.options) {
      if (
        selectedAddress &&
        !localidadesOptions?.includes(l => selectedAddress.localidad.id === l.value)
      ) {
        setLocalidadesOptions([
          {
            value: selectedAddress.localidad.id,
            label: selectedAddress.localidad.nombre,
            postal_code: selectedAddress.postal_code,
          },
          ...localidadesSelectProps.options,
        ]);
      } else {
        setLocalidadesOptions(localidadesSelectProps.options || []);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, localidadesSelectProps.options]);

  return (
    <div className="ShipmentAddressForm">
      {!!company && dataSource && dataSource.length > 0 && (
        <Table
          {...tableProps}
          className="ShipmentAddressFormTable"
          size="small"
          rowKey="id"
          scroll={{ x: 500, y: 150 }}
          pagination={false}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedAddress?.id ? [selectedAddress.id] : [],
            onChange: (props, selectedRows) => {
              lockReset.current = true;

              const selectedRow = selectedRows[0];

              if (!localidadesOptions?.includes(l => selectedRow.localidad.id === l.value)) {
                setLocalidadesOptions([
                  ...(localidadesOptions || []),
                  {
                    value: selectedRow.localidad.id,
                    label: selectedRow.localidad.nombre,
                    postal_code: selectedRow.postal_code,
                  },
                ]);
              }

              if (form) {
                form.setFieldsValue({
                  receiver_address: selectedRow.address,
                  receiver_email: selectedRow.email,
                  receiver_phone: selectedRow.phone_number,
                  receiver_name: selectedRow.contact,
                  receiver_pais: selectedRow.localidad.provincia.pais.id,
                  receiver_provincia: selectedRow.localidad.provincia.id,
                  receiver_localidad: selectedRow.localidad.id,
                  receiver_postal_code: selectedRow.postal_code,
                });
              }

              setSelectedAddress(selectedRow);
              setSaveAddress(false);
            },
          }}>
          <Table.Column dataIndex="description" title={t('Direcciones guardadas')} />
        </Table>
      )}

      <div className="ShipmentFormRow">
        <Form.Item label={t('País')} name={'receiver_pais'}>
          <AntdSelect {...paisesSelectProps} allowClear showSearch />
        </Form.Item>
        <Form.Item label={t('Provincia')} name={'receiver_provincia'}>
          <AntdSelect {...provinciasSelectProps} disabled={!pais} />
        </Form.Item>
      </div>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Localidad')}
          name={'receiver_localidad'}
          rules={[
            { required: true, message: t('La {{field}} es requerida', { field: t('localidad') }) },
          ]}>
          <AntdSelect
            {...localidadesSelectProps}
            options={localidadesOptions}
            onChange={(v, opt) => {
              localidadesSelectProps.onChange?.(v, opt);
              if (form && initialValuesSetted && opt) {
                form.setFieldsValue({
                  receiver_postal_code: (opt as any).item?.postal_code,
                });
              }
            }}
            disabled={!pais || !provincia}
            allowClear
            showSearch
          />
        </Form.Item>
        <Form.Item
          label={t('Código postal')}
          name={'receiver_postal_code'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('Código postal') }),
            },
          ]}>
          <Input />
        </Form.Item>
      </div>
      <Form.Item
        label={t('Dirección')}
        name={'receiver_address'}
        rules={[
          {
            required: true,
            message: t('La {{field}} es requerida', { field: t('dirección') }),
          },
        ]}>
        <Input />
      </Form.Item>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Email')}
          name={'receiver_email'}
          rules={[
            { type: 'email', message: t('Email inválido') },
            { required: true, message: t('El {{field}} es requerido', { field: t('email') }) },
          ]}>
          <Input />
        </Form.Item>
        <Form.Item
          label={t('Número de Teléfono')}
          name={'receiver_phone'}
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('número de teléfono') }),
            },
            {
              validateTrigger: 'onBlur',
              validator: (_, value) => {
                if (!value || !countryCode) {
                  return Promise.resolve();
                }

                const { regexValidation } = phoneNumberMeta[countryCode] || {};

                if (regexValidation && !regexValidation.test(value)) {
                  return Promise.reject(
                    new Error(t('Número de teléfono inválido para el país seleccionado')),
                  );
                }

                return Promise.resolve();
              },
            },
          ]}>
          <Input prefix={phoneNumberMeta[countryCode]?.phoneCountry} />
        </Form.Item>
      </div>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Nombre y apellido del destinatario')}
          name={'receiver_name'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', {
                field: t('Nombre y apellido del destinatario'),
              }),
            },
          ]}>
          <Input />
        </Form.Item>
      </div>
      <div className="ShipmentFormRow">
        <Form.Item
          label={t('Instrucciones para la entrega')}
          tooltip={t('Se imprimirá en la etiqueta de envío')}
          name={'receiver_delivery_instructions'}>
          <Input />
        </Form.Item>
      </div>
    </div>
  );
};

export default ShipmentAddressForm;
